import Loader from 'components/loaders/Loader';
import { ShouldRender } from 'components/ShouldRender';
import React, { useContext, useState } from 'react';
import { useLaundrySchedules } from 'services/laundry/schedules';
import { useSubmitResourceBooking } from 'services/laundry/submit-resource-booking';
import { isBefore, parse } from 'date-fns';
import { useLocale } from 'services/user/locale/hook';
import { useNonBookableSlots, isSlotBooked, scheduleToSlotFormat } from 'services/laundry/non-bookable-slots';
import { useIntl } from 'react-intl';
import { getDateFromNow } from '../DateSelectionSection';
import { useLaundryBookings } from '../../../../../../../../services/laundry/bookings';
import { LaundryBrikkModalContext } from '../../../LaundryBrikkModalContext';
import { ScheduleCard } from './components/ScheduleCard';
import styles from './styles.module.scss';

const capitalizeWords = (str) => str.replace(/\b[a-zA-ZåäöÅÄÖ]+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

function formatDate(date, locale, options) {
  const formatted = new Date(date)
    .toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      ...options,
    })
    .replace(',', '');
  return capitalizeWords(formatted);
}

export function WhatAndWhenSection() {

  const intl = useIntl();

  const { selectedDate, selectedEndDate, selectedResource } = useContext(
    LaundryBrikkModalContext,
  );
  const { laundrySchedules, isFetching: isFetchingForLaundrySchedules } = useLaundrySchedules(
    selectedResource?.id,
  );
  const [submittedSchedule, setSubmittedSchedule] = useState();
  const { submitResourceBooking, isLoading } = useSubmitResourceBooking();
  const { locale } = useLocale();

  const minDate = getDateFromNow(laundrySchedules?.bookingRule?.notAfter);
  const maxDate = getDateFromNow(laundrySchedules?.bookingRule?.notBefore);

  const updatedSelectedDate = isBefore(selectedDate, minDate) ? minDate : selectedDate;

  const dayOfWeek = updatedSelectedDate ? updatedSelectedDate.toLocaleDateString('en-GB', {
    weekday: 'long',
  }) : '';

  const formattedDate = formatDate(updatedSelectedDate, locale, {
    weekday: 'short',
  });
  const formattedEndDate = formatDate(selectedEndDate, locale);

  const { laundryBookings, isFetching: isFetchingFromLaundryBookings } = useLaundryBookings(
    selectedResource?.id,
  );

  const { bookedSlotsByDate } = useNonBookableSlots(selectedResource?.id);

  // Helper to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear()
      && d1.getMonth() === d2.getMonth()
      && d1.getDate() === d2.getDate();
  };

  const isScheduleAvailable = (schedule) => {
    const isBookedByUser = laundryBookings?.some((laundryBooking) => {
      const isApartmentType = selectedResource?.type === 'apartment';
      if (isApartmentType) {
        return (
          isSameDay(laundryBooking?.fullDate, updatedSelectedDate)
          && `${schedule?.from}-${schedule?.to}` === '11.00-11.00'
        );
      }
      return (
        isSameDay(laundryBooking?.fullDate, updatedSelectedDate)
        && `${schedule?.from}-${schedule?.to}` === `${laundryBooking?.from}-${laundryBooking?.to}`
      );
    });

    const isBookedByOthers = isSlotBooked(bookedSlotsByDate, updatedSelectedDate, scheduleToSlotFormat(schedule?.from, schedule?.to));

    const formatScheduleTo = schedule?.to.replace('.', ':');
    const scheduleEnd = parse(formatScheduleTo, 'HH:mm', selectedDate);

    const isScheduleBeforeMaxDate = isBefore(scheduleEnd, maxDate);

    return laundrySchedules?.available
      && dayOfWeek === schedule?.day
      && (laundrySchedules?.scheduleType === 'daily' ? !!selectedEndDate : true)
      && !isBookedByUser
      && !isBookedByOthers
      && isScheduleBeforeMaxDate;
  };

  const hasAvailableSchedules = !!laundrySchedules?.schedules?.some(isScheduleAvailable);

  if (isFetchingForLaundrySchedules && isFetchingFromLaundryBookings) return <Loader />;

  return (
    <div className={styles.cardsSectionWrapper}>
      {hasAvailableSchedules && laundrySchedules?.schedules?.map((schedule, index) => {
        const showAvailableLaundrySchedules = isScheduleAvailable(schedule);

        return (
          <ShouldRender
            key={index}
            condition={showAvailableLaundrySchedules}
          >
            <ScheduleCard
              date={formattedDate}
              address={selectedResource?.address}
              from={schedule?.from}
              to={schedule?.to}
              dateTo={formattedEndDate}
              showRange={laundrySchedules?.scheduleType === 'daily'}
              isLoading={submittedSchedule === index && isLoading}
              onClick={() => {
                setSubmittedSchedule(index);
                submitResourceBooking({
                  date: updatedSelectedDate,
                  endDate: selectedEndDate,
                  resourceId: selectedResource?.id,
                  from: schedule?.from,
                  to: schedule?.to,
                  scheduleType: laundrySchedules?.scheduleType,
                });
              }}
            />
          </ShouldRender>
        );
      })}
      <ShouldRender condition={!hasAvailableSchedules}>
        <p className={styles.noAvailability}>
          {intl.formatMessage({ id: 'noAvailabilityForPeriod' })}
        </p>
      </ShouldRender>
    </div>
  );
}
